import React from 'react';
import clsx from 'clsx';

// ========================================
// CARD COMPONENT - Professional Design
// ========================================

export const Card = ({ children, className, padding = 'p-3 lg:p-4', ...props }) => (
  <div
    className={clsx(
      'bg-white rounded-xl border border-secondary-100/60',
      'shadow-sm hover:shadow-md transition-shadow duration-200',
      padding,
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// ========================================
// BUTTON COMPONENT - Professional Design
// ========================================

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className,
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  ...props
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:from-primary-700 hover:to-primary-600 shadow-md hover:shadow-lg',
    secondary: 'bg-secondary-700 text-white hover:bg-secondary-800 shadow-md hover:shadow-lg',
    outline: 'bg-white border-2 border-secondary-200 text-secondary-700 hover:border-primary-300 hover:bg-primary-50/30',
    ghost: 'bg-transparent text-secondary-600 hover:bg-secondary-100 hover:text-secondary-800',
    danger: 'bg-gradient-to-r from-danger-600 to-danger-500 text-white hover:from-danger-700 hover:to-danger-600 shadow-md hover:shadow-lg',
    success: 'bg-gradient-to-r from-success-600 to-success-500 text-white hover:from-success-700 hover:to-success-600 shadow-md hover:shadow-lg',
  };

  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs rounded-lg min-h-[30px] lg:min-h-[32px]',
    md: 'px-3.5 py-2 text-sm rounded-lg min-h-[36px] lg:min-h-[38px]',
    lg: 'px-5 py-2.5 text-sm rounded-xl min-h-[42px]',
  };

  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center gap-1.5',
        'font-semibold transition-all duration-200',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-primary-500/40',
        'active:scale-[0.98]',
        'whitespace-nowrap',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {!loading && icon && iconPosition === 'left' && <span className="w-4 h-4">{icon}</span>}
      <span>{children}</span>
      {!loading && icon && iconPosition === 'right' && <span className="w-4 h-4">{icon}</span>}
    </button>
  );
};

// ========================================
// INPUT COMPONENT - Professional Design
// ========================================

export const Input = ({ label, error, icon, rightIcon, className, type = 'text', ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}</label>}
    <div className="relative">
      {icon && (
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-400">{icon}</div>
      )}
      <input
        type={type}
        className={clsx(
          'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm',
          'bg-white text-secondary-900 placeholder:text-secondary-400',
          'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
          'hover:border-secondary-300',
          error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
          icon && 'pl-10',
          rightIcon && 'pr-10',
          'transition-all duration-200'
        )}
        {...props}
      />
      {rightIcon && (
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2">{rightIcon}</div>
      )}
    </div>
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// TEXTAREA COMPONENT - Professional Design
// ========================================

export const Textarea = ({ label, error, className, rows = 4, ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}</label>}
    <textarea
      rows={rows}
      className={clsx(
        'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm resize-y',
        'bg-white text-secondary-900 placeholder:text-secondary-400',
        'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
        'hover:border-secondary-300',
        error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
        'transition-all duration-200'
      )}
      {...props}
    />
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// SELECT COMPONENT - Professional Design
// ========================================

export const Select = ({ label, error, options, placeholder, className, ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}</label>}
    <div className="relative">
      <select
        className={clsx(
          'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm appearance-none bg-white',
          'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
          'hover:border-secondary-300',
          error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
          'transition-all duration-200 cursor-pointer'
        )}
        {...props}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// BADGE COMPONENT - Professional Design
// ========================================

export const Badge = ({ children, variant = 'default', size = 'sm', className }) => {
  const variants = {
    default: 'bg-secondary-100 text-secondary-700 border-secondary-200',
    primary: 'bg-primary-100 text-primary-700 border-primary-200',
    success: 'bg-success-100 text-success-700 border-success-200',
    warning: 'bg-warning-100 text-warning-700 border-warning-200',
    danger: 'bg-danger-100 text-danger-700 border-danger-200',
    info: 'bg-info-100 text-info-700 border-info-200',
    new: 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border-primary-200',
    follow_up: 'bg-gradient-to-r from-warning-50 to-warning-100 text-warning-700 border-warning-200',
    cold: 'bg-gradient-to-r from-info-50 to-info-100 text-info-700 border-info-200',
    warm: 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 border-orange-200',
    hot: 'bg-gradient-to-r from-danger-50 to-danger-100 text-danger-700 border-danger-200',
    converted: 'bg-gradient-to-r from-success-50 to-success-100 text-success-700 border-success-200',
    closed: 'bg-gradient-to-r from-secondary-50 to-secondary-100 text-secondary-600 border-secondary-200',
    not_connected: 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-200',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs rounded-lg',
    md: 'px-2.5 py-1 text-xs rounded-lg',
    lg: 'px-3 py-1.5 text-sm rounded-xl',
  };

  return (
    <span className={clsx(
      'inline-flex items-center font-semibold border',
      variants[variant] || variants.default,
      sizes[size],
      className
    )}>
      {children}
    </span>
  );
};

// ========================================
// STAT CARD COMPONENT - Professional Design
// ========================================

export const StatCard = ({ title, value, icon, trend, trendValue, color = 'primary' }) => {
  const colors = {
    primary: { icon: 'bg-gradient-to-br from-primary-500 to-primary-600', ring: 'ring-primary-500/20' },
    secondary: { icon: 'bg-gradient-to-br from-secondary-500 to-secondary-600', ring: 'ring-secondary-500/20' },
    success: { icon: 'bg-gradient-to-br from-success-500 to-success-600', ring: 'ring-success-500/20' },
    warning: { icon: 'bg-gradient-to-br from-warning-500 to-warning-600', ring: 'ring-warning-500/20' },
    danger: { icon: 'bg-gradient-to-br from-danger-500 to-danger-600', ring: 'ring-danger-500/20' },
    info: { icon: 'bg-gradient-to-br from-info-500 to-info-600', ring: 'ring-info-500/20' },
  };

  const colorStyles = colors[color] || colors.primary;

  return (
    <Card className="py-3 lg:py-4 hover:shadow-lg transition-all duration-200 group">
      <div className="flex items-center gap-3">
        <div className={clsx(
          'w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg',
          colorStyles.icon,
          'ring-4',
          colorStyles.ring
        )}>
          <span className="text-white text-base lg:text-lg">{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs lg:text-sm text-secondary-500 font-medium truncate">{title}</p>
          <p className="text-xl lg:text-2xl font-bold text-secondary-900">{value}</p>
          {trend && (
            <span className={clsx(
              'text-xs font-semibold inline-flex items-center gap-0.5',
              trend === 'up' ? 'text-success-600' : 'text-danger-600'
            )}>
              {trend === 'up' ? '↑' : '↓'} {trendValue}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

// ========================================
// TABLE COMPONENT - Professional Design
// ========================================

export const Table = ({ children, className }) => (
  <div className="overflow-x-auto">
    <table className={clsx('w-full', className)}>{children}</table>
  </div>
);

export const TableHead = ({ children, className }) => (
  <thead className={clsx('bg-gradient-to-r from-secondary-50 to-secondary-100/50 border-b-2 border-secondary-200/50', className)}>{children}</thead>
);

export const TableBody = ({ children, className }) => (
  <tbody className={clsx('divide-y divide-secondary-100', className)}>{children}</tbody>
);

export const TableRow = ({ children, className, onClick, hoverable = true }) => (
  <tr
    className={clsx(
      'transition-all duration-150',
      hoverable && 'hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent',
      onClick && 'cursor-pointer',
      className
    )}
    onClick={onClick}
  >
    {children}
  </tr>
);

export const TableCell = ({ children, className, header = false }) => (
  <td
    className={clsx(
      'px-4 py-3 lg:px-5 lg:py-3.5',
      header ? 'text-left text-xs font-bold text-secondary-600 uppercase tracking-wider' : 'text-sm text-secondary-700',
      className
    )}
  >
    {children}
  </td>
);

// ========================================
// LOADING SPINNER
// ========================================

export const Spinner = ({ size = 'md', className }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
  return (
    <div className={clsx('animate-spin text-primary-600', sizes[size], className)}>
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  );
};

// ========================================
// EMPTY STATE - Professional Design
// ========================================

export const EmptyState = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6">
    {icon && (
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary-100 to-secondary-50 flex items-center justify-center mb-4 shadow-inner">
        {icon}
      </div>
    )}
    <h3 className="text-lg font-bold text-secondary-900 mb-2">{title}</h3>
    {description && <p className="text-sm text-secondary-500 text-center max-w-sm mb-4">{description}</p>}
    {action}
  </div>
);

// ========================================
// MODAL COMPONENT - Professional Design
// ========================================

export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
    full: 'sm:max-w-full sm:mx-4',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-gradient-to-br from-secondary-900/60 to-secondary-800/40 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx(
        'relative bg-white w-full sm:rounded-2xl shadow-2xl',
        'min-h-screen sm:min-h-0 sm:max-h-[90vh]',
        'overflow-y-auto animate-scale-in',
        sizes[size],
        'border border-secondary-200/50'
      )}>
        <div className="sticky top-0 bg-gradient-to-r from-white to-secondary-50/50 border-b border-secondary-200/50 px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-secondary-900">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// ========================================
// TABS COMPONENT - Professional Design
// ========================================

export const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="border-b-2 border-secondary-200/50 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
    <div className="flex gap-1 -mb-0.5 min-w-max">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={clsx(
            'px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all duration-200',
            activeTab === tab.value
              ? 'border-primary-600 text-primary-700 bg-primary-50/50 rounded-t-lg'
              : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50/50'
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className={clsx(
              'ml-2 px-2 py-0.5 rounded-full text-xs font-bold',
              activeTab === tab.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-secondary-100 text-secondary-600'
            )}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  </div>
);

// ========================================
// SEARCH INPUT - Professional Design
// ========================================

export const SearchInput = ({ value, onChange, placeholder = 'Search...', className }) => (
  <div className={clsx('relative', className)}>
    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-secondary-200 bg-white text-sm text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200"
    />
  </div>
);

// ========================================
// AVATAR COMPONENT - Professional Design
// ========================================

export const Avatar = ({ name, src, size = 'md', className }) => {
  const sizes = {
    sm: 'w-7 h-7 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-11 h-11 text-base',
    xl: 'w-14 h-14 text-lg',
  };

  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return src ? (
    <img src={src} alt={name} className={clsx('rounded-full object-cover ring-2 ring-white shadow-md', sizes[size], className)} />
  ) : (
    <div className={clsx(
      'rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-semibold flex items-center justify-center shadow-md ring-2 ring-white',
      sizes[size],
      className
    )}>
      {initials}
    </div>
  );
};

// ========================================
// PAGE HEADER COMPONENT - Professional Design
// ========================================

export const PageHeader = ({ title, description, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
    <div>
      <h1 className="text-xl lg:text-2xl font-bold text-secondary-900">{title}</h1>
      {description && <p className="text-sm text-secondary-500 mt-0.5">{description}</p>}
    </div>
    {action && <div className="flex gap-2">{action}</div>}
  </div>
);

// ========================================
// IMAGE PREVIEW MODAL - in-app lightbox
// ========================================
// Helper: does this attachment look like an image?
export const isImageAttachment = (att) => {
  const mime = String(att?.mimeType || att?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const ext = String(att?.url || att?.fileName || att?.originalName || '')
    .split('?')[0]
    .split('.')
    .pop()
    .toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
};

export const ImagePreviewModal = ({ src, alt, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 animate-scale-in"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        title="Close"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <img
        src={src}
        alt={alt || 'Attachment'}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[92vh] object-contain rounded-lg shadow-2xl"
      />
    </div>
  );
};